import type { Placement, ReferenceType } from '@floating-ui/react';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import type { Dispatch, ReactNode, RefObject, SetStateAction } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useTooltipContext } from '../context/TooltipContext';

type UseTooltipReturn = {
  anchorProps: Record<string, unknown>;
  floatingProps: Record<string, unknown>;
  arrowEl: ReactNode;
  anchorRef: RefObject<ReferenceType | null>;
  floatingRef: RefObject<ReferenceType | null>;
  isActive: boolean;
  setIsBlockedByChild: Dispatch<SetStateAction<boolean>>;
};

export const useTooltip = (
  truncatedTextMode?: boolean,
  ariaRole: 'tooltip' | 'label' = 'tooltip',
  enabled = true,
  placement: Placement = 'bottom',
  decorative = false, // visual only: hidden from screen readers, not linked to anchor
): UseTooltipReturn => {
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const { disabled: contextDisabled } = useTooltipContext();
  const isEnabled = enabled && !contextDisabled;
  const [isBlockedByChild, setIsBlockedByChild] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const { setBlocking: setParentBlocking } = useTooltipContext();

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (o) => (!o || isEnabled) && setIsOpen(o),
    strategy: 'fixed',
    placement,
    middleware: [
      offset(10),
      flip(),
      shift(),

      arrow({
        element: arrowRef,
      }),
      size({
        padding: 10,
        apply({ availableWidth, elements }) {
          const maxWidth = Math.min(availableWidth, window.innerWidth / 3);
          elements.floating.style.maxWidth = `${maxWidth}px`;
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: ariaRole,
    enabled: !decorative,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const arrowEl = useMemo(() => {
    return <FloatingArrow ref={arrowRef} context={context} className="fill-primary-100" />;
  }, [context]);

  useEffect(() => {
    // Children-popups can block parent-popups (no two tooltips active at the same time)
    setParentBlocking(isOpen);
  }, [isOpen, setParentBlocking]);

  useEffect(() => {
    if (!truncatedTextMode) return;

    const anchor = refs.reference.current as HTMLElement;
    if (!anchor) return;

    const truncatedEls = Array.from(anchor.querySelectorAll('.truncate, [class*="line-clamp-"], .middle-truncate'));
    if (
      anchor.classList.contains('truncate') ||
      Array.from(anchor.classList).some((c) => c.startsWith('line-clamp-')) ||
      anchor.classList.contains('middle-truncate')
    ) {
      truncatedEls.push(anchor);
    }

    const anyTruncated = truncatedEls.some(
      (el) =>
        el.scrollWidth > el.clientWidth ||
        el.scrollHeight > el.clientHeight ||
        (!!el.getAttribute('data-original') && el.getAttribute('data-original') !== el.textContent),
    );

    setIsTruncated(anyTruncated);
  }, [isOpen, refs.reference, truncatedTextMode]);

  return {
    // A tab stop only when there is a tooltip to reveal; otherwise it's a dead stop.
    anchorProps: { ref: refs.setReference, tabIndex: isEnabled ? 0 : undefined, ...getReferenceProps() },
    floatingProps: {
      ref: refs.setFloating,
      ...getFloatingProps({
        'aria-hidden': decorative || undefined,
        style: {
          wordBreak: 'break-word',
          hyphens: 'auto',
          zIndex: 9999,
          ...floatingStyles,
        },
      }),
    },
    arrowEl,
    anchorRef: refs.reference,
    floatingRef: refs.floating,
    isActive: isOpen && !isBlockedByChild && (truncatedTextMode ? isTruncated : true),
    setIsBlockedByChild,
  };
};
